import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, Share } from "lucide-react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

type Props = {
  className?: string;
  variant?: React.ComponentProps<typeof Button>["variant"];
  size?: React.ComponentProps<typeof Button>["size"];
  style?: React.CSSProperties;
};

export function InstallPWAButton({ className, variant = "outline", size, style }: Props) {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [iosHintOpen, setIosHintOpen] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;
  // On non-iOS, hide until the browser fires beforeinstallprompt (criteria met).
  if (!ios && !deferred) return null;

  const handleClick = async () => {
    if (deferred) {
      await deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      return;
    }
    if (ios) setIosHintOpen(true);
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        style={style}
        onClick={handleClick}
      >
        <Download className="mr-2 h-4 w-4" />
        {t("help.installBtn")}
      </Button>

      <Dialog open={iosHintOpen} onOpenChange={setIosHintOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Přidat Tipomatic na plochu</DialogTitle>
            <DialogDescription>
              Na iPhonu/iPadu se aplikace instaluje ze Safari:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-2 text-sm list-decimal list-inside">
            <li>
              Klepni dole na ikonu <Share className="inline h-4 w-4 mx-1" />
              <strong>Sdílet</strong>.
            </li>
            <li>
              Zvol <strong>Přidat na plochu</strong>.
            </li>
            <li>
              Potvrď <strong>Přidat</strong> vpravo nahoře.
            </li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
